import { useEffect } from 'react';

import { useAuth } from '@/shared/auth/hooks/use-auth';
import { setFinanceAuthHeadersProvider } from './client';

/** Inputs for registering the finance request auth provider. */
interface IConfigureFinanceAuthArgs {
  isDev: boolean;
  backendUrl: string | undefined;
  isAuthenticated: boolean;
  getToken: () => Promise<string>;
}

/**
 * Builds the finance auth-header provider from a token getter. Kept pure so it
 * is unit-testable without React/Auth0.
 */
export function buildFinanceAuthProvider(getToken: () => Promise<string>) {
  return async () => ({ Authorization: `Bearer ${await getToken()}` });
}

/** Returns whether a development backend URL points at this machine. */
export function isLocalFinanceBackend(backendUrl: string | undefined): boolean {
  if (!backendUrl) return true;
  try {
    const hostname = new URL(backendUrl).hostname;
    return ['localhost', '127.0.0.1', '::1', '0.0.0.0'].includes(hostname);
  } catch {
    return false;
  }
}

/** Registers auth for remote finance backends and no auth for the local demo. */
export function configureFinanceAuth({
  isDev,
  backendUrl,
  isAuthenticated,
  getToken,
}: IConfigureFinanceAuthArgs): (() => void) | undefined {
  if (isDev && isLocalFinanceBackend(backendUrl)) {
    setFinanceAuthHeadersProvider(() => ({}));
    return;
  }
  if (!isAuthenticated) return;

  setFinanceAuthHeadersProvider(buildFinanceAuthProvider(getToken));
  return () => setFinanceAuthHeadersProvider(() => ({}));
}

/**
 * Registers Bumblebee's Auth0 access token on the finance client once
 * authenticated. This is the permanent auth seam — it stays valid when AIF is
 * replaced by our own backends, which accept the same token. Renders nothing.
 */
export function FinanceAuthBridge() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth();

  useEffect(
    () =>
      configureFinanceAuth({
        isDev: import.meta.env.DEV,
        backendUrl: import.meta.env.VITE_FINANCE_BACKEND_URL,
        isAuthenticated,
        getToken: () => getAccessTokenSilently(),
      }),
    [isAuthenticated, getAccessTokenSilently],
  );

  return null;
}
